
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import { formatAmount, useFormatters } from '@financialharmony/ui';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArrowLeft, Calendar, FileText, Banknote, Check, X, ListFilter } from 'lucide-react';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import type { BankAccountSummary } from '@/types/accounts';
import type { Transaction } from '@/types/payments';
import { useFeedbackContext } from '@/contexts/useFeedbackContext';
import { userFacingErrorMessage } from '@/utils/userFacingError';

// multi-provider-banking F8/AC6: read-only display label for the provider backing this account —
// a consultant/platform admin can see it, but no action here forces re-resolution (that exists
// only via F9's service-role API). No shared mapping exists elsewhere in src/ for this — kept
// local since it's a one-off presentation concern for exactly the three known providers.
const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  ztl: 'ZTL',
  enablebanking: 'Enable Banking',
  openpayments: 'OpenPayments',
};

function providerDisplayName(provider: string): string {
  return PROVIDER_DISPLAY_NAMES[provider] ?? provider;
}

const RECENT_TRANSACTIONS_PAGE_SIZE = 10;

type AccountState =
  | { kind: 'loading' }
  | { kind: 'found'; account: BankAccountSummary }
  // An unknown id, or another company's: getBankAccounts() only returns the active company's
  // accounts, so both read as not found (FIN-473).
  | { kind: 'not_found' }
  | { kind: 'failed' };

type TransactionsState =
  | { kind: 'loading' }
  | { kind: 'loaded'; transactions: Transaction[] }
  | { kind: 'failed'; error: unknown };

const BankAccountDetails = () => {
  const { id: accountId } = useParams();
  const navigate = useNavigate();
  const t = useTranslations('consultantTransactions.accountDetails');
  // Each result remembers what it was loaded for, so a new id or a retry reads as loading
  // without resetting state inside the effect.
  const [loadedAccount, setLoadedAccount] = useState<{ forId: string | undefined; state: AccountState } | null>(null);
  const [transactionsAttempt, setTransactionsAttempt] = useState(0);
  const [loadedTransactions, setLoadedTransactions] = useState<{ key: string; state: TransactionsState } | null>(null);
  const accountState: AccountState = loadedAccount?.forId === accountId ? loadedAccount.state : { kind: 'loading' };

  // FIN-869 (F6): registers { bankAccountId } as feedback page context for as long as this
  // page is mounted — unlike a selection that outlives its own page, this lives exactly as
  // long as the record it describes.
  useFeedbackContext(accountId ? { bankAccountId: accountId } : {});

  useEffect(() => {
    let cancelled = false;
    ServiceRegistry.getAccountService()
      .getBankAccounts()
      .then((accounts) => {
        if (cancelled) return;
        const foundAccount = accounts.find((acc) => acc.id === accountId);
        setLoadedAccount({
          forId: accountId,
          state: foundAccount ? { kind: 'found', account: foundAccount } : { kind: 'not_found' },
        });
      })
      .catch(() => {
        if (!cancelled) setLoadedAccount({ forId: accountId, state: { kind: 'failed' } });
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const foundAccountId = accountState.kind === 'found' ? accountState.account.id : null;
  const transactionsKey = `${foundAccountId}:${transactionsAttempt}`;
  const transactionsState: TransactionsState =
    loadedTransactions?.key === transactionsKey ? loadedTransactions.state : { kind: 'loading' };

  useEffect(() => {
    if (!foundAccountId) return;
    let cancelled = false;
    const key = `${foundAccountId}:${transactionsAttempt}`;
    ServiceRegistry.getTransactionService()
      .getTransactionsForAccount(foundAccountId, 1, RECENT_TRANSACTIONS_PAGE_SIZE)
      .then((result) => {
        if (!cancelled) setLoadedTransactions({ key, state: { kind: 'loaded', transactions: result.transactions } });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLoadedTransactions({ key, state: { kind: 'failed', error } });
      });
    return () => {
      cancelled = true;
    };
  }, [foundAccountId, transactionsAttempt]);

  const backButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => navigate('/cash-management')}
      className="flex items-center gap-1"
    >
      <ArrowLeft size={16} />
      {t('back')}
    </Button>
  );

  if (accountState.kind === 'failed') {
    return <div>{t('loadFailed')}</div>;
  }

  if (accountState.kind === 'loading') {
    return <div>{t('loading')}</div>;
  }

  if (accountState.kind === 'not_found') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">{backButton}</div>
        <Alert>
          <AlertTitle>{t('notFoundTitle')}</AlertTitle>
          <AlertDescription>{t('notFoundBody')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const { account } = accountState;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        {backButton}
        <h1 className="text-3xl font-bold tracking-tight">{account.bankName}</h1>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('overview')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{t('accountNumber')}</p>
              <p className="font-medium">{account.maskedNumber ?? t('noAccountNumber')}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{t('currentBalance')}</p>
              {/* null while the account can't be read -- R-ISO-1 (FIN-577) */}
              {account.balance === null ? (
                <p className="font-medium text-lg text-muted-foreground">{t('unavailable')}</p>
              ) : (
                <p className="font-medium text-lg">
                  {formatAmount(account.balance, account.currency)}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{t('currency')}</p>
              <p className="font-medium">{account.currency}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{t('provider')}</p>
              <p className="font-medium">{providerDisplayName(account.provider)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('recent')}</CardTitle>
            <Button
              variant="outline"
              onClick={() => navigate(`/transactions?accountId=${accountId}`)}
              className="flex items-center gap-2"
            >
              <ListFilter size={16} />
              {t('seeFull')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <RecentTransactions
            state={transactionsState}
            onRetry={() => setTransactionsAttempt((attempt) => attempt + 1)}
          />
        </CardContent>
      </Card>
    </div>
  );
};

function RecentTransactions({ state, onRetry }: { state: TransactionsState; onRetry: () => void }) {
  const t = useTranslations('consultantTransactions.accountDetails');
  const { formatDate } = useFormatters();
  if (state.kind === 'loading') {
    return <p className="text-sm text-muted-foreground">{t('loadingTransactions')}</p>;
  }
  if (state.kind === 'failed') {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t('transactionsFailedTitle')}</AlertTitle>
        <AlertDescription className="space-y-3">
          <p>{userFacingErrorMessage(state.error, t('transactionsFailed'))}</p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t('tryAgain')}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }
  if (state.transactions.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('noTransactions')}</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('columns.date')}</TableHead>
          <TableHead>{t('columns.description')}</TableHead>
          <TableHead>{t('columns.reference')}</TableHead>
          <TableHead>{t('columns.amount')}</TableHead>
          <TableHead>{t('columns.status')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {state.transactions.map((transaction) => {
          const booked = transaction.booking_status !== 'reserved';
          return (
            <TableRow key={transaction.bankTransactionId ?? transaction.id}>
              <TableCell className="flex items-center gap-2">
                <Calendar size={16} className="text-muted-foreground" />
                {formatDate(transaction.date, 'date')}
              </TableCell>
              <TableCell>{transaction.description}</TableCell>
              <TableCell className="flex items-center gap-2">
                <FileText size={16} className="text-muted-foreground" />
                {transaction.structuredReference ?? '—'}
              </TableCell>
              <TableCell className={`font-medium ${transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                <div className="flex items-center gap-2">
                  <Banknote size={16} />
                  {formatAmount(transaction.amount, transaction.currency)}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant={booked ? 'default' : 'outline'} className="flex items-center gap-1 w-fit">
                  {booked ? <Check size={12} className="text-green-500" /> : <X size={12} className="text-orange-500" />}
                  {booked ? t('booked') : t('unbooked')}
                </Badge>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export default BankAccountDetails;
